import { logger } from '@inboundx/shared/platform';
import { Loader2, MessageSquarePlus, Square, SquareDashedMousePointer } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import {
    captureViewport,
    dataUrlToBase64,
    FEEDBACK_UI_ATTRIBUTE,
} from '@/components/feedback/capture';
import { buildCssSelector, describeElement } from '@/components/feedback/cssSelector';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { useOptionalWorkspace } from '@/contexts/WorkspaceContext';
import { cn } from '@/lib/utils';
import { createVisualFeedback, type FeedbackSeverity } from '@/services/visualFeedback';

const SEVERITIES: { value: FeedbackSeverity; label: string }[] = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'important', label: 'Important' },
    { value: 'urgent', label: 'Urgent' },
];

interface Draft {
    mode: 'page' | 'element';
    screenshot: string;
    selector: string;
    context: string;
}

interface VisualFeedbackWidgetProps {
    /** Onboarding and the welcome flow have no header to sit in, so the trigger
     *  floats over the top-right corner of the page there instead. */
    floating?: boolean;
    /** Positions the trigger explicitly; modals pin it beside their close button. */
    className?: string;
}

export function VisualFeedbackWidget({
    floating = false,
    className,
}: VisualFeedbackWidgetProps = {}) {
    const { backofficeUser, isRealStaff } = useBackofficeAuth();
    const { user } = useAuth();
    // Optional: the widget also renders on onboarding and the welcome flow,
    // which are outside the WorkspaceProvider.
    const selectedDomain = useOptionalWorkspace()?.selectedDomain ?? null;
    const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;

    const [menuOpen, setMenuOpen] = useState(false);
    const [picking, setPicking] = useState(false);
    const [hovered, setHovered] = useState<DOMRect | null>(null);
    const [draft, setDraft] = useState<Draft | null>(null);
    const [feedback, setFeedback] = useState('');
    const [severity, setSeverity] = useState<FeedbackSeverity>('normal');
    const [sending, setSending] = useState(false);
    const [capturing, setCapturing] = useState(false);

    // Open to every signed-in user, in every environment: the trigger is a
    // discreet header icon, and the function behind it already accepts any
    // Supabase session (it checks domain access, not staff). Only the "Open in
    // Linear" shortcut on the success toast stays staff-only.
    const enabled = user !== null;

    const capture = useCallback(async (mode: 'page' | 'element', element?: Element) => {
        // Rendering the DOM to an image takes a few seconds on a busy page, so the
        // button has to show progress or the click looks like it did nothing.
        setCapturing(true);
        try {
            const rect = element?.getBoundingClientRect();
            // Rendering blocks the main thread, so let the browser paint the
            // pending state first — otherwise the UI just freezes on "Feedback".
            await new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve))
            );
            const screenshot = await captureViewport(rect);
            setDraft({
                mode,
                screenshot,
                selector: element ? buildCssSelector(element) : '',
                context: element ? describeElement(element) : '',
            });
        } catch (error) {
            logger.error('Visual feedback capture failed', error);
            toast.error('Could not capture the screen', {
                description: 'Please try again.',
            });
        } finally {
            setCapturing(false);
        }
    }, []);

    // Element picking: hover to outline, click to select, Escape to cancel.
    useEffect(() => {
        if (!picking) return;

        const isFeedbackUi = (element: Element) => element.closest(`[${FEEDBACK_UI_ATTRIBUTE}]`);

        // An open modal makes everything behind it unreachable to the cursor:
        // Radix locks `pointer-events: none` on the body, and its overlay covers
        // the page on top of that. Both would make every hover outside the modal
        // report the modal itself instead of the element the user is pointing at.
        // Picking is read-only, and the handlers below swallow the interaction
        // that would dismiss the modal, so lift both for the duration of the pick.
        const { body } = document;
        const lockedPointerEvents = body.style.pointerEvents;
        body.style.pointerEvents = 'auto';

        // Restoring only what is still ours: on unmount the modal puts its own
        // value back before this cleanup runs, and overwriting it would leave the
        // page unclickable.
        const restore = (element: HTMLElement, ours: string, locked: string) => () => {
            if (element.style.pointerEvents === ours) element.style.pointerEvents = locked;
        };

        const restoreBody = restore(body, 'auto', lockedPointerEvents);
        const restoreOverlays = [
            ...document.querySelectorAll<HTMLElement>(
                '[data-slot="dialog-overlay"], [data-slot="sheet-overlay"]'
            ),
        ].map((overlay) => {
            const locked = overlay.style.pointerEvents;
            overlay.style.pointerEvents = 'none';
            return restore(overlay, 'none', locked);
        });

        const onMove = (event: MouseEvent) => {
            const target = event.target as Element | null;
            if (!target || isFeedbackUi(target)) {
                setHovered(null);
                return;
            }
            setHovered(target.getBoundingClientRect());
        };

        const onClick = (event: MouseEvent) => {
            const target = event.target as Element | null;
            if (!target || isFeedbackUi(target)) return;
            event.preventDefault();
            event.stopPropagation();
            setPicking(false);
            setHovered(null);
            void capture('element', target);
        };

        // On `window`, not `document`: Radix dismisses a dialog from a capturing
        // `document` listener registered when it opened, so while picking inside
        // a modal Escape would close the modal — and discard whatever the user
        // had typed in it — instead of cancelling the pick. Window capture runs
        // first, and stopping propagation there keeps the escape to ourselves.
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            event.stopPropagation();
            setPicking(false);
            setHovered(null);
        };

        // Radix dismisses on pointerdown, so without this a pick outside the
        // modal would close it before the click that selects the element lands.
        const onPointerDown = (event: Event) => {
            const target = event.target as Element | null;
            if (!target || isFeedbackUi(target)) return;
            event.stopPropagation();
        };

        document.addEventListener('mousemove', onMove, true);
        document.addEventListener('pointerdown', onPointerDown, true);
        document.addEventListener('mousedown', onPointerDown, true);
        document.addEventListener('click', onClick, true);
        window.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('mousemove', onMove, true);
            document.removeEventListener('pointerdown', onPointerDown, true);
            document.removeEventListener('mousedown', onPointerDown, true);
            document.removeEventListener('click', onClick, true);
            window.removeEventListener('keydown', onKeyDown, true);
            restoreBody();
            for (const restoreOverlay of restoreOverlays) restoreOverlay();
        };
    }, [picking, capture]);

    const closeDraft = () => {
        setDraft(null);
        setFeedback('');
        setSeverity('normal');
    };

    const submit = async () => {
        if (!draft || !feedback.trim()) return;
        setSending(true);
        try {
            const result = await createVisualFeedback({
                issue_type: 'visual_feedback',
                feedback: feedback.trim(),
                target_mode: draft.mode,
                severity,
                domain: selectedDomain ?? '',
                page_url: window.location.href,
                reporter_name: backofficeUser?.display_name ?? '',
                reporter_email: backofficeUser?.email ?? user?.email ?? '',
                environment,
                app_version: typeof __APP_VERSION__ === 'undefined' ? 'unknown' : __APP_VERSION__,
                viewport_width: window.innerWidth,
                viewport_height: window.innerHeight,
                element_selector: draft.selector,
                element_context: draft.context,
                screenshot_filename: `feedback-${draft.mode}.jpg`,
                screenshot_content_type: 'image/jpeg',
                screenshot_base64: dataUrlToBase64(draft.screenshot),
            });

            toast.success('Feedback sent', {
                description: result.issue_identifier ?? undefined,
                action:
                    isRealStaff && result.issue_url
                        ? {
                              label: 'Open in Linear',
                              onClick: () => window.open(result.issue_url ?? '', '_blank'),
                          }
                        : undefined,
            });
            closeDraft();
        } catch (error) {
            logger.error('Visual feedback submission failed', error);
            toast.error('Could not send feedback', {
                description: error instanceof Error ? error.message : 'Unknown error',
            });
        } finally {
            setSending(false);
        }
    };

    if (!enabled) return null;

    return (
        <div
            {...{ [FEEDBACK_UI_ATTRIBUTE]: '' }}
            className={className ?? (floating ? 'fixed right-4 top-4 z-50' : undefined)}
        >
            {/* Portalled to the body: a modal centres itself with a transform, and
                a transformed ancestor becomes the containing block for `fixed`
                children — inside a dialog the outline and the hint would be placed
                against the dialog instead of the viewport. */}
            {picking &&
                createPortal(
                    <div {...{ [FEEDBACK_UI_ATTRIBUTE]: '' }}>
                        {hovered && (
                            <div
                                className="pointer-events-none fixed z-[9999] border-2 border-[#ff2d55] bg-[#ff2d55]/10"
                                style={{
                                    top: hovered.top,
                                    left: hovered.left,
                                    width: hovered.width,
                                    height: hovered.height,
                                }}
                            />
                        )}
                        <div className="pointer-events-none fixed inset-x-0 top-4 z-[9999] flex justify-center">
                            <span className="rounded-md bg-foreground px-3 py-1.5 text-xs text-background">
                                Click an element to report it — Escape to cancel
                            </span>
                        </div>
                    </div>,
                    document.body
                )}

            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        aria-label={capturing ? 'Capturing…' : 'Send feedback'}
                        title="Send feedback"
                        disabled={capturing}
                        // Same affordance as the What's New bell it sits next to.
                        className={cn(
                            'flex h-7 w-7 items-center justify-center rounded-md text-foreground-subtle transition-colors hover:bg-sidebar-accent hover:text-foreground',
                            floating &&
                                'h-9 w-9 rounded-full border border-border bg-card/80 text-foreground-subtle shadow-sm backdrop-blur-sm'
                        )}
                    >
                        {capturing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <MessageSquarePlus className="h-4 w-4" />
                        )}
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Send feedback</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={() => void capture('page')}>
                        <Square className="mr-2 h-4 w-4" />
                        Whole page
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setPicking(true)}>
                        <SquareDashedMousePointer className="mr-2 h-4 w-4" />
                        Select element
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog
                open={draft !== null}
                onOpenChange={(open) => {
                    if (!open && !sending) closeDraft();
                }}
            >
                <DialogContent className="max-w-lg" showFeedbackButton={false}>
                    <DialogHeader>
                        <DialogTitle>Send feedback</DialogTitle>
                        <DialogDescription>
                            {draft?.mode === 'element'
                                ? 'Reporting the highlighted element.'
                                : 'Reporting the current page.'}
                        </DialogDescription>
                    </DialogHeader>

                    {draft && (
                        <img
                            src={draft.screenshot}
                            alt="Captured screenshot"
                            className="max-h-56 w-full rounded-md border object-contain"
                        />
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="visual-feedback-text">What is wrong?</Label>
                        <Textarea
                            id="visual-feedback-text"
                            value={feedback}
                            onChange={(event) => setFeedback(event.target.value)}
                            placeholder="Describe the problem or the change you expect."
                            rows={4}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="visual-feedback-severity">Severity</Label>
                        <Select
                            value={severity}
                            onValueChange={(value) => setSeverity(value as FeedbackSeverity)}
                        >
                            <SelectTrigger id="visual-feedback-severity">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {SEVERITIES.map((item) => (
                                    <SelectItem key={item.value} value={item.value}>
                                        {item.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <DialogFooter>
                        <Button variant="ghost" onClick={closeDraft} disabled={sending}>
                            Cancel
                        </Button>
                        <Button onClick={submit} disabled={sending || !feedback.trim()}>
                            {sending ? 'Sending…' : 'Send'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
